import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import Column from '@amzn/meridian/column'
import { IFRAME_HEIGHT, IFRAME_PARENT_CONTAINER_HEIGHT } from '@utils/ui-config'
import { store } from '@atredux/store'
import { setSeawardIFrame, seawardIFrameLoadingStatus } from '@atredux/slices/iframeSlice'
import { getIsSeawardFrameLoading } from '@atredux/selectors/iframeSelector'
import { getLastNonMigratedTool, getSelectedTool } from '@atredux/selectors/toolSelector'
import { seawardTool } from '@services/ToolMenuConfigService'

interface ISeawardIFrameControllerProps {
    hide?: boolean
}

/**
 * Responsible for rendering the tool/Seaward iframes and hiding
 * whenever appropriate. When a new tool is selected, it should
 * be loaded in this iframe.
 *
 * Usage:
 * <IFrameController tool={selectedTool} hide />
 *
 * @param {boolean} hide Flag to decide if the iframe should be hidden.
 * @returns {React.ReactElement}
 */
const SeawardIFrameController = ({
    hide,
}: Readonly<ISeawardIFrameControllerProps>): React.ReactElement => {
    const seawardIFrame: React.RefObject<HTMLIFrameElement> = useRef<HTMLIFrameElement>(null)
    const selectedTool = useSelector(getSelectedTool)
    const lastNonMigratedTool = useSelector(getLastNonMigratedTool)
    const isSeawardFrameLoading: seawardIFrameLoadingStatus = useSelector(getIsSeawardFrameLoading)

    useEffect(() => {
        // Store a plain snapshot ({ current }) rather than the live React ref.
        // Redux Toolkit's Immer deep-freezes state; freezing the live ref makes
        // its `current` read-only, so React throws `"current" is read-only` when
        // it nulls the ref on unmount (e.g. on logout). The snapshot preserves the
        // `{ current }` shape consumers expect while leaving the live ref writable.
        store.dispatch(setSeawardIFrame({ current: seawardIFrame.current }))
        return () => {
            store.dispatch(setSeawardIFrame(null))
        }
    }, [seawardIFrame])

    useEffect(() => {
        const seawardIframe = document.getElementsByClassName('seaward-iframe-container') as
            HTMLCollectionOf<HTMLElement>
        seawardIframe.item(0).style.display = 'none'
    }, [])

    return (
        <Column className="seaward-iframe-container" height={
            selectedTool.showMigrated || (isSeawardFrameLoading !== seawardIFrameLoadingStatus.SUCCESS) ? '0vh' : IFRAME_PARENT_CONTAINER_HEIGHT
        } heights={['fill']}>
            <iframe
                aria-label={seawardTool.displayName}
                title={seawardTool.displayName}
                src={seawardTool.path}
                ref={seawardIFrame}
                role="presentation"
                style={{ display: 'inline', height: IFRAME_HEIGHT }}
            />
        </Column>
    )
}

export default SeawardIFrameController
